import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { WeekHours } from '@generated/tcea/models';
import { startOfDay, parse, differenceInSeconds, isAfter, isBefore, isSameSecond } from 'date-fns';

/**
 * Returns a Validator function that makes the field required if the predicate function returns true
 *
 * Usage: new FormControl('', requiredIfValidator(() => this.form.get('otherField').value))
 */
export function requiredIfValidator(predicate: () => boolean): ValidatorFn {
  return (formControl: UntypedFormControl) => {
    if (predicate()) {
      return Validators.required(formControl);
    }
    return undefined;
  };
}

export function minimumFormItemsValidator(number: number): ValidatorFn {
  return (formControl: UntypedFormControl) => {
    if (Object.keys(formControl.value).length < number) {
      return { min: { value: number } };
    }
    return undefined;
  };
}

/**
 * Returns a Validator function that validates at least one field in a FormGroup has a value.
 * This is useful for cases where you need "at least one of these fields" validation.
 *
 * Usage:
 * promotionIds: new UntypedFormGroup({
 *   brink: new UntypedFormControl(''),
 *   xenial: new UntypedFormControl(''),
 *   'xenial-corp': new UntypedFormControl('')
 * }, { validators: atLeastOneRequiredValidator() })
 */
export function atLeastOneRequiredValidator(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    if (!(formGroup instanceof UntypedFormGroup)) {
      return null;
    }

    const controls = formGroup.controls;
    const hasAtLeastOneValue = Object.keys(controls).some((key) => {
      const value = controls[key].value;
      return value !== null && value !== undefined && value !== '';
    });

    return hasAtLeastOneValue ? null : { atLeastOneRequired: true };
  };
}

export function makeFieldRequiredIfPopulated(
  parentForm: UntypedFormGroup,
  controlName: string,
): any {
  return (formControl: UntypedFormControl) => {
    const controlForField = parentForm.get(controlName);

    if (controlForField) {
      controlForField.clearValidators();

      if (formControl.value) {
        controlForField.setValidators([Validators.required]);
      }

      controlForField.updateValueAndValidity({ emitEvent: false });
    }
  };
}

/* function: timeBeforeValidator - Returns a validator "beforeTime". It compares start time and end time from two fields
 * and add to the validators error list if end time is before the start time. Error validation can be setup by capturing
 * formControl errors. example : item.get('startTime').errors?.beforeTime
 */
export function timeBeforeValidator(endTimeControl: AbstractControl): ValidatorFn {
  return (startTimeControl: AbstractControl): { [key: string]: any } | null => {
    if (endTimeControl.value) {
      const baseDate = startOfDay(new Date());
      const endTime = parse(endTimeControl.value, 'HH:mm', baseDate);
      const startTime = parse(startTimeControl.value, 'HH:mm', baseDate);

      const endSeconds = differenceInSeconds(endTime, baseDate);
      const startSeconds = differenceInSeconds(startTime, baseDate);

      if (endSeconds <= startSeconds) {
        return { beforeTime: { value: endSeconds } };
      }
    }
    return undefined;
  };
}

/**
 * Returns a Validator function that validates the given control is greater than this control
 *
 * Usage: minField.setValidators([minField.validator, lessThanValidator(maxField)]);
 * (where minField and maxField are FormControls)
 *
 * @param maxControl - control that should be the max
 */
export function lessThanValidator(maxControl: AbstractControl): ValidatorFn {
  return (minControl: AbstractControl): { [key: string]: any } | null => {
    const max = parseFloat(maxControl.value);
    const min = parseFloat(minControl.value);
    if ((min || min === 0) && (max || max === 0)) {
      if (max < min) {
        return { lessThan: { value: max } };
      }
    }
    return undefined;
  };
}

/* function: timeAfterValidator - Returns a validator "afterTime". It compares start time and end time from two fields
 * and add to the validators error list if start time time is after the end time. Error validation can be setup by
 * capturing formControl errors. example : item.get('startTime').errors?.afterTime
 */
export function timeAfterValidator(startTimeControl: AbstractControl): ValidatorFn {
  return (endTimeControl: AbstractControl): { [key: string]: any } | null => {
    if (startTimeControl.value) {
      const baseDate = startOfDay(new Date());
      const endTime = parse(endTimeControl.value, 'HH:mm', baseDate);
      const startTime = parse(startTimeControl.value, 'HH:mm', baseDate);

      const endSeconds = differenceInSeconds(endTime, baseDate);
      const startSeconds = differenceInSeconds(startTime, baseDate);

      if (endSeconds <= startSeconds) {
        return { afterTime: { value: startSeconds } };
      }
    }
    return undefined;
  };
}

/**
 * Returns a Validator function that validates the given control is less than this control
 *
 * Usage: maxField.setValidators([maxField.validator, greaterThanValidator(minField)]);
 * (where minField and maxField are FormControls)
 *
 * @param maxControl - control that should be the min
 */
export function greaterThanValidator(minControl: AbstractControl): ValidatorFn {
  return (maxControl: AbstractControl): { [key: string]: any } | null => {
    const max = parseFloat(maxControl.value);
    const min = parseFloat(minControl.value);
    if ((min || min === 0) && (max || max === 0)) {
      if (max < min) {
        return { greaterThan: { value: min } };
      }
    }
    return undefined;
  };
}

/**
 * Validates that the current control's value is between the values of minControl and maxControl.
 * You can use this validator for scenarios like ensuring defaultQuantity is between min and max.
 *
 * @param minControl - The form control for the minimum value
 * @param maxControl - The form control for the maximum value
 * @param options - Optional config to set inclusive or exclusive bounds
 */
export function inBetweenValidator(
  minControl: AbstractControl,
  maxControl: AbstractControl,
  options: { inclusive?: boolean } = { inclusive: true },
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = parseFloat(control.value);
    const min = parseFloat(minControl.value);
    const max = parseFloat(maxControl.value);

    if ((min || min === 0) && (max || max === 0) && (value || value === 0)) {
      const inclusive = options.inclusive;
      const isValid = inclusive ? value >= min && value <= max : value > min && value < max;

      if (!isValid) {
        return {
          inBetween: {
            value,
            min,
            max,
            inclusive,
          },
        };
      }
    }
    return null;
  };
}

/**
 * Returns a Validator function that validates that there is no white space at the beginning of the string.
 * Usage: group.addControl(field.property, new FormControl('', [noLeadingWhiteSpace]));
 */
export function noLeadingWhiteSpace(control: AbstractControl): ValidationErrors | undefined {
  const leadingWhiteSpace = typeof control.value === 'string' && /^\s/.test(control.value);
  return leadingWhiteSpace ? { whiteSpace: true } : undefined;
}

/**
 * Returns a Validator function that validates that there is no space in the entered string.
 * Usage: group.addControl(field.property, new FormControl('', [noSpaceAllowed]));
 */
export function noSpaceAllowed(control: AbstractControl): ValidationErrors | null {
  const value = control.value as string;
  if (value && /\s/.test(value)) {
    return { noSpaceAllowed: true };
  }
  return null;
}

/**
 * Returns a Validator function that validates that control time is greater than the min date specified
 * Usage: this.form.addControl(
        'controlName,
        new FormControl('', [Validators.required, timeAfter('minValue')]),
      );
 */
export function timeAfter(min: Date): ValidatorFn {
  return (c: AbstractControl) => {
    if (c == null || c.value == null) return null;
    let isValid = c.value >= min;
    if (isValid) {
      return null;
    } else {
      return { timeAfterError: true };
    }
  };
}

/**
 * This utility is used to check if there is overlapping time ranges for any day of th given hours template.
 * hours object should be in the this format
 * {
    "THU": [{"startTime": "7:01", "endTime": "20:01"}],
    "TUE": [{"startTime": "7:01", "endTime": "20:01"},
            {"startTime": "4:00", "endTime": "16:00"},
            {"startTime": "2:00", "endTime": "15:00"}],
    }
 * Returns true if there is overlapping hours in the hours object.
 */
export function timeOverlap(hours: WeekHours) {
  for (const key in hours) {
    if (hours[key].length <= 1) continue;

    for (const item of hours[key]) {
      // loop over everything
      for (const element of hours[key]) {
        // loop over what hasn't been covered by the above array
        const { startTime, endTime } = item;
        if (timeBetween(startTime, element)) return true;
        if (timeBetween(endTime, element)) return true;
      }
    }
  }
  return false;
}

/**
* This utility is used to validatre if there is overlapping time ranges for any day of th given special hours list.
* hours object should be in the this format
* [{"startTime": "7:01", "endTime": "20:01"}],
   {"startTime": "7:01", "endTime": "20:01"},
   }
* Returns true if there is overlapping hours in the hours object.
 */
export function specialHoursOverlap(specialHoursList): boolean {
  for (let item of specialHoursList) {
    for (let element of specialHoursList) {
      if (specialHoursList.indexOf(item) !== specialHoursList.indexOf(element)) {
        const { startTime, endTime } = item;
        if (timeBetween(startTime, element)) return true;
        if (timeBetween(endTime, element)) return true;
      }
    }
  }
  return false;
}

// checks and returns boolean if the a given time is in-between the range startTime and endTime
export function timeBetween(time: string, range: { startTime: string; endTime: string }): boolean {
  const baseDate = startOfDay(new Date());
  const timeToCheck = parse(time, 'HH:mm', baseDate);
  const rangeStart = parse(range.startTime, 'HH:mm', baseDate);
  const rangeEnd = parse(range.endTime, 'HH:mm', baseDate);

  return (
    isSameSecond(timeToCheck, rangeStart) ||
    isSameSecond(timeToCheck, rangeEnd) ||
    (isAfter(timeToCheck, rangeStart) && isBefore(timeToCheck, rangeEnd))
  );
}

// convert time string to seconds since start of day
export function toSecs(time: string): number {
  const baseDate = startOfDay(new Date());
  const timeDate = parse(time, 'HH:mm', baseDate);
  return differenceInSeconds(timeDate, baseDate);
}

/**
 * Helper function to trim leading and trailing spaces from string properties
 * of catalog items (e.g., title, name, description, shortTitle, decorators, etc.)
 */
export function trimCatalogItemFields<T>(item: T): T {
  if (typeof item === 'string') {
    return item.trim() as unknown as T;
  }
  if (!item || typeof item !== 'object') {
    return item;
  }
  if (typeof File !== 'undefined' && item instanceof File) {
    return item;
  }
  if (typeof Blob !== 'undefined' && item instanceof Blob) {
    return item;
  }
  if (Array.isArray(item)) {
    return item.map((elem) => trimCatalogItemFields(elem)) as unknown as T;
  }
  const result: any = {};
  for (const key of Object.keys(item)) {
    const val = (item as any)[key];
    if (typeof val === 'string') {
      result[key] = val.trim();
    } else if (
      val &&
      typeof val === 'object' &&
      !(val instanceof Date) &&
      !(typeof File !== 'undefined' && val instanceof File) &&
      !(typeof Blob !== 'undefined' && val instanceof Blob)
    ) {
      result[key] = trimCatalogItemFields(val);
    } else {
      result[key] = val;
    }
  }
  return result as T;
}
